<template>
    <Modal class="mellon-webecr-api-keys-modal"
           data-component="MellonWebECRApiKeysModal"
           :cancel="cancel"
           cancel-label="CLOSE"
           icon="fa-solid fa-tablet"
           title="MELLON_WEBECR_API_KEYS_MODAL.MODAL_TITLE">
        <ErrorMessage v-if="error"
                      :error="error" />

        <div class="form-h">
            <div class="form-group">
                <label class="control-label">
                    {{ l('MELLON_WEBECR_API_KEYS_MODAL.REDEEM__LABEL') }}
                </label>
                <div class="form-h__right">
                    <div class="flex flex--align-center">
                        <SquareBtn size="lg"
                                   color="info"
                                   @click="openRedeemModal()">
                            <i class="fa-solid fa-square-plus"></i>
                            {{ l('MELLON_WEBECR_API_KEYS_MODAL.REDEEM__BTN') }}
                        </SquareBtn>
                    </div>
                </div>
            </div>
        </div>

        <hr />

        <template v-if="apiKeys.length > 0">
            <table class="table table-striped text-left table--valign-middle">
                <thead>
                    <tr>
                        <th>{{ l('MELLON_WEBECR_API_KEYS_MODAL.API_KEY') }}</th>
                        <th>{{ l('MELLON_WEBECR_API_KEYS_MODAL.LABEL') }}</th>
                        <th>{{ l('MELLON_WEBECR_API_KEYS_MODAL.REDEEMED_AT') }}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="apiKeyData in displayApiKeys"
                        :key="apiKeyData.redeemedAt">
                        <td>
                            <small>{{ maskFilter(apiKeyData.apiKey, { showStart: 6, maskChar: '*'}) }}</small>
                        </td>

                        <td>
                            {{ apiKeyData.label }}
                        </td>

                        <td>
                            {{ dateFilter(apiKeyData.redeemedAt) }}
                        </td>

                        <td class="text-right no-wrap">
                            <SquareBtn v-if="selectedApiKey === apiKeyData.apiKey"
                                       color="primary"
                                       :disabled="true">
                                {{ l('SELECTED') }}
                            </SquareBtn>
                            <template v-else>
                                <SquareBtn color="danger"
                                           class="btn-icon"
                                           :loading="deletingApiKey && deletingApiKey.apiKey === apiKeyData.apiKey"
                                           @click="deleteApiKey(apiKeyData)">
                                    <i class="fa-solid fa-trash" />
                                </SquareBtn>
                                &nbsp;
                                <SquareBtn color="primary"
                                           @click="selectApiKey(apiKeyData)">
                                    {{ l('SELECT') }}
                                </SquareBtn>
                            </template>
                        </td>
                    </tr>
                </tbody>
            </table>
        </template>
        <div v-else
             class="alert alert-info">
            {{ l('MELLON_WEBECR_API_KEYS_MODAL.NO_API_KEYS') }}
        </div>
    </Modal>
</template>

<script>
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import Modal from '@/vue/components/Modals/Modal';
import PaymentMethodRef from '@/lib/PaymentMethods/PaymentMethodRef';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';

export default {
    components: {
        ErrorMessage,
        SquareBtn,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        selectedApiKey: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            /**
             * @type {MellonWebECRApiKeyData[]}
             */
            apiKeys: [],

            /**
             * @type {?MellonWebECRApiKeyData}
             */
            deletingApiKey: null,

            /**
             * @type {PaymentMethod}
             */
            paymentMethod: null,

            /**
             * @type {object}
             */
            paymentMethodConfig: null,

            /**
             * @type {?Error}
             */
            error: null,
        };
    },

    computed: {
        displayApiKeys() {
            // We want to display the API keys in reverse order, so the most recent one is at the top.
            return this.apiKeys.slice().reverse();
        },
    },

    created() {
        this.loadApiKeysFromPaymentMethodConfig();
    },

    methods: {
        loadApiKeysFromPaymentMethodConfig() {
            // Extract existing API keys from the company payment method config.
            this.paymentMethod = container.paymentMethodService.getByRef(PaymentMethodRef.CARD_MELLON_WEBECR);
            this.paymentMethodConfig = this.paymentMethod.getConfig();

            if (this.paymentMethodConfig && this.paymentMethodConfig.apiKeys) {
                // Trust the entries are in the correct format. Not much we can do about that.
                this.apiKeys = this.paymentMethodConfig.apiKeys;
            }
        },

        openRedeemModal() {
            container.vueModalFactory
                .open(MODAL_NAMES__EPOS.MELLON_WEBECR_REDEEM)
                .then(
                    /**
                     * @param {MellonWebECRRedeemModalResult} result
                     */
                    async (result) => {
                        console.log('Redemption result', result);

                        // The redemption process completed successfully.
                        // We now have a new API for a terminal. Store this in the company payment method config
                        // so we don't lose it and can get it back later.

                        /**
                         * @type {MellonWebECRApiKeyData}
                         */
                        const apiKeyData = {
                            apiKey: result.response.Id,
                            label: result.label,
                            redeemedAt: result.redeemedAt.toISOString(),
                        };

                        // Tell the DigiTickets API to save the new key.

                        // List of JSON Patch operations to apply to the payment method config.
                        const patchOperations = [];

                        // If the current config has no apiKeys property, add one.
                        if (!this.paymentMethodConfig.hasOwnProperty('apiKeys')) {
                            patchOperations.push(
                                {
                                    op: 'add',
                                    path: '/apiKeys',
                                    value: [],
                                }
                            );
                        }

                        // Add the new API key to the list of API keys.
                        patchOperations.push(
                            {
                                op: 'add',
                                path: '/apiKeys/-',
                                value: apiKeyData,
                            }
                        );

                        // Save the new API key to the company payment method config.
                        // Note that we are updating the *company* payment method config here, not the
                        // device payment method config! This is unusual. It happens because we don't want to
                        // lose any API keys that have been generated when a device changes terminal.
                        await this.patchCompanyPaymentMethodConfig(patchOperations);
                    },
                )
                .catch(suppressModalCancelledErrorOrReThrow)
                .catch((error) => {
                    this.error = error;
                });
        },

        /**
         * @param {MellonWebECRApiKeyData} apiKeyData
         */
        async deleteApiKey(apiKeyData) {
            const confirmed = await container.dialogService
                .confirm('MELLON_WEBECR_API_KEYS_MODAL.CONFIRM_DELETE');

            if (!confirmed) {
                return;
            }

            this.deletingApiKey = apiKeyData;

            // Remember we are in the settings modal here, so the user should already have permission
            // to manage device settings.

            const index = this.apiKeys.findIndex((key) => key.apiKey === apiKeyData.apiKey);
            if (index === -1) {
                return;
            }

            const patchOperations = [
                {
                    op: 'remove',
                    path: '/apiKeys/' + index,
                },
            ];

            await this.patchCompanyPaymentMethodConfig(patchOperations);
        },

        async patchCompanyPaymentMethodConfig(patchOperations) {
            const companyID = container.userService.company.ID;
            const paymentMethodID = this.paymentMethod.ID;
            const { data: newConfig } = await container.apiClient.request(
                'PATCH',
                `companies/${companyID}/paymentmethods/${paymentMethodID}/config`,
                patchOperations,
                true,
                {
                    'Content-Type': 'application/json-patch+json',
                }
            );
            console.log('Updated company payment method config', newConfig);

            // Set the new config to the payment method.
            this.paymentMethod.setConfig(newConfig);

            // Re-save the user to local storage so the new config is saved locally.
            container.userService.saveToCache();

            this.loadApiKeysFromPaymentMethodConfig();

            this.deletingApiKey = null;

            return newConfig;
        },

        /**
         * @param {MellonWebECRApiKeyData} apiKeyData
         */
        selectApiKey(apiKeyData) {
            this.resolve({ apiKeyData });
        },
    },
};
</script>
